import {useEffect, useRef, useState} from 'react';
import {ACCOUNT_ID} from '../../api/account';
import {generateAudioPreview} from '../../api/audio';
import {createSubscription, createTopup} from '../../api/payment';
import {getYandexClientId, trackEvent} from '../../utils/analytics';
import {showError} from '../../utils/notify';
import {CLOUDPAYMENTS_PUBLIC_ID, PAYMENT_CURRENCY} from '../../config/constants';
import LinkAccountButton from '../account/LinkAccountButton';

const TIER_LABELS = {
    lite: 'Личный',
    pro: 'Семейный',
};

const TIER_DESCRIPTIONS = {
    lite: '1 видео со звездой, 3 трека и 2 сказки в месяц. Достаточно, чтобы тронуть того, кто дорог.',
    pro: '3 видео со звездой, 10 треков и 5 сказок в месяц. Хватит на всех близких — с простором для экспериментов.',
};

const TOPUP_DESCRIPTIONS = {
    'plan_1': 'Дополнительные генерации',
    'plan_3': 'Лучшая цена за единицу',
    'plan_5': 'Большой запас генераций',
    'plan_10': 'Максимальный пакет',
};

const PAYWALL_EXAMPLES = [
    {
        id: 'ex_burunov',
        kind: 'video',
        title: 'Актёр А.',
        subtitle: 'Юбилей, поздравления',
        gradient: 'from-[#FF6B6B] via-[#E84855] to-[#A23B47]',
        thumbnail: '/celebratory/sergei-burunov.jpg',
        videoUrl: 'https://storage.yandexcloud.net/selebrus-page-assets/burunov.mp4',
    },
    {
        id: 'ex_skazka_aunt',
        kind: 'skazki',
        title: 'Сказка перед сном',
        subtitle: 'Тёплый голос тёти',
        gradient: 'from-[#F4E1C9] via-[#E5C6A0] to-[#C9A074]',
        thumbnail: '/skazki/bedtime.jpg',
        audioUrl: 'https://storage.yandexcloud.net/selebrus-page-assets/Examples/skazki/voice-aunt.mp3',
    },
    {
        id: 'ex_rock',
        kind: 'music',
        title: 'Рок',
        subtitle: 'Для друга',
        gradient: 'from-[#FFD3D3] via-[#FFB8C7] to-[#FF9CC0]',
        audioUrl: 'https://storage.yandexcloud.net/selebrus-page-assets/Examples/music/rock.mp3',
    },
    {
        id: 'ex_urgant',
        kind: 'video',
        title: 'Ведущий В.',
        subtitle: 'Шуточное',
        gradient: 'from-[#5C6B3A] via-[#3F4A26] to-[#1F2812]',
        thumbnail: '/celebratory/Иван Ургант.jpg',
        videoUrl: 'https://storage.yandexcloud.net/selebrus-page-assets/Main/urgant.mp4',
    },
    {
        id: 'ex_skazka_sister',
        kind: 'skazki',
        title: 'Волшебная история',
        subtitle: 'Голос старшей сестры',
        gradient: 'from-[#E8D5FF] via-[#C9A8F0] to-[#9B7CC8]',
        thumbnail: '/skazki/Magic.jpg',
        audioUrl: 'https://storage.yandexcloud.net/selebrus-page-assets/Examples/skazki/voice-sister.mp3',
    },
    {
        id: 'ex_pop',
        kind: 'music',
        title: 'Поп',
        subtitle: 'Для девушки',
        gradient: 'from-[#FF8FA0] via-[#FF6B6B] to-[#C8485A]',
        audioUrl: 'https://storage.yandexcloud.net/selebrus-page-assets/Examples/music/pop.mp3',
    },
    {
        id: 'ex_andreeva',
        kind: 'video',
        title: 'Ведущая Е.',
        subtitle: 'Тёплое поздравление',
        gradient: 'from-[#7B9ECC] via-[#4F73A8] to-[#2C4870]',
        thumbnail: '/celebratory/Екатерина Андреева.jpg',
        videoUrl: 'https://storage.yandexcloud.net/selebrus-page-assets/Main/andreeva.mp4',
    },
    {
        id: 'ex_skazka_uncle',
        kind: 'skazki',
        title: 'Приключение',
        subtitle: 'Бархатный голос дяди',
        gradient: 'from-[#D6E5C7] via-[#A8C490] to-[#6E8E5A]',
        thumbnail: '/skazki/adventure.jpg',
        audioUrl: 'https://storage.yandexcloud.net/selebrus-page-assets/Examples/skazki/voice-uncle.mp3',
    },
    {
        id: 'ex_electro',
        kind: 'music',
        title: 'Электро',
        subtitle: 'Для вечеринки',
        gradient: 'from-[#A0C4FF] via-[#6EA0FF] to-[#3B6FD9]',
        audioUrl: 'https://storage.yandexcloud.net/selebrus-page-assets/Examples/music/electro.mp3',
    },
];

const EXAMPLE_BADGE = {
    video: 'видео',
    music: 'песня',
    skazki: 'сказка',
};

const PAYWALL_GUARANTEES = [
    {
        title: 'Гарантия возврата 30 дней',
        subtitle: 'Передумали — вернём без вопросов',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 3-6.7L3 8"/>
                <path d="M3 3v5h5"/>
            </svg>
        ),
    },
    {
        title: 'Мгновенная доставка',
        subtitle: 'Файлы в личном кабинете сразу после оплаты',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round">
                <path d="M13 2L3 14h9l-1 8 10-12h-9z"/>
            </svg>
        ),
    },
    {
        title: 'Безопасная оплата',
        subtitle: 'СБП, карты · ЮКасса · мы за этим',
        icon: (
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                 strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2"/>
                <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
        ),
    },
];

function tierKey(plan) {
    const id = plan.id || '';
    if (id.includes('pro')) return 'pro';
    return 'lite';
}

const FORMAT_ICONS = {
    video: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
             strokeLinecap="round" strokeLinejoin="round">
            <rect x="2" y="6" width="14" height="12" rx="2"/>
            <path d="M22 8l-6 4 6 4z"/>
        </svg>
    ),
    music: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
             strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18V5l12-2v13"/>
            <circle cx="6" cy="18" r="3"/>
            <circle cx="18" cy="16" r="3"/>
        </svg>
    ),
    book: (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
             strokeLinecap="round" strokeLinejoin="round">
            <path d="M2 4h6a4 4 0 014 4v14a3 3 0 00-3-3H2zM22 4h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z"/>
        </svg>
    ),
};


const WAVEFORM = [30, 55, 75, 90, 65, 45, 80, 95, 70, 50, 35, 60, 85, 100, 75, 55, 40, 70, 90, 80, 60, 45, 65, 85, 70, 50, 35, 55, 75, 60];

function PlayerCard({
                        title,
                        subtitle,
                        durationLabel,
                        isPlaying,
                        isLoading,
                        onToggle,
                        accentClass = 'from-brand-red to-brand-orange'
                    }) {
    return (
        <div className="bg-gray-50 rounded-2xl p-3 sm:p-4 flex items-center gap-3 border border-gray-100">
            <button
                onClick={onToggle}
                disabled={isLoading || !onToggle}
                className={`w-11 h-11 rounded-full bg-gradient-to-br ${accentClass} flex items-center justify-center shrink-0 disabled:opacity-50 hover:opacity-90 transition`}
            >
                {isLoading ? (
                    <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"
                                fill="none"/>
                        <path className="opacity-75" fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
                    </svg>
                ) : isPlaying ? (
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="white">
                        <rect x="6" y="4" width="4" height="16" rx="1"/>
                        <rect x="14" y="4" width="4" height="16" rx="1"/>
                    </svg>
                ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="white">
                        <path d="M8 5v14l11-7z"/>
                    </svg>
                )}
            </button>
            <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-2 mb-1">
                    <p className="text-sm font-semibold text-brand-dark truncate">{title}</p>
                    {durationLabel && <span className="text-xs text-brand-dark/40 shrink-0">{durationLabel}</span>}
                </div>
                {subtitle && <p className="text-xs text-brand-dark/50 mb-2 truncate">{subtitle}</p>}
                <div className="flex items-center gap-[2px] h-4">
                    {WAVEFORM.map((h, i) => (
                        <div
                            key={i}
                            className={`flex-1 rounded-full transition-colors ${isPlaying ? 'bg-brand-red' : 'bg-brand-red/25'}`}
                            style={{height: `${h}%`}}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
}

export default function Paywall({
                                    plans = {subscriptions: [], topups: []},
                                    subscription = null,
                                    credits,
                                    musicCredits = 0,
                                    skazkiCredits = 0,
                                    linkedTelegramId = null,
                                    linkedMaxId = null,
                                    preSelectedPersona,
                                    preSelectedText,
                                    context = 'video',
                                    onPurchaseSuccess,
                                    onClearPreSelection,
                                }) {
    const subscriptionPlans = plans.subscriptions || [];
    const topupPlans = plans.topups || [];

    const hasActiveSubscription = !!subscription
        && ['active', 'cancel_at_period_end', 'failed'].includes(subscription.status);

    const monthlyPlans = subscriptionPlans.filter(p => p.billingPeriod === 'monthly');
    const litePlan = monthlyPlans.find(p => tierKey(p) === 'lite');
    const liteTotal = litePlan ? (litePlan.videos + (litePlan.songs || 0) + (litePlan.tales || 0)) : 0;
    const litePerGreeting = liteTotal > 0 ? litePlan.price / liteTotal : 0;

    const [selectedSubId, setSelectedSubId] = useState(null);
    const [selectedTopupId, setSelectedTopupId] = useState(null);
    const [isPurchasing, setIsPurchasing] = useState(false);
    const [audioPreviewLoading, setAudioPreviewLoading] = useState(false);
    const [audioPreviewUrl, setAudioPreviewUrl] = useState(null);
    const [audioPreviewError, setAudioPreviewError] = useState(null);
    const [headerPlaying, setHeaderPlaying] = useState(null);
    const [showYooKassaWidget, setShowYooKassaWidget] = useState(false);
    const [purchaseError, setPurchaseError] = useState(null);
    const [examplePlayingId, setExamplePlayingId] = useState(null);
    const [exampleVideo, setExampleVideo] = useState(null);

    const headerAudioRef = useRef(null);
    const exampleAudioRef = useRef(null);
    const yookassaWidgetRef = useRef(null);

    useEffect(() => {
        if (!selectedSubId && monthlyPlans.length > 0) {
            const pro = monthlyPlans.find(p => tierKey(p) === 'pro') || monthlyPlans[0];
            setSelectedSubId(pro.id);
        }
    }, [monthlyPlans, selectedSubId]);

    useEffect(() => {
        if (!selectedTopupId && topupPlans.length > 0) {
            setSelectedTopupId(topupPlans.find(p => p.id === 'plan_3')?.id || topupPlans[0].id);
        }
    }, [topupPlans, selectedTopupId]);

    useEffect(() => {
        if (!preSelectedText || !preSelectedPersona) {
            setAudioPreviewUrl(prev => {
                if (prev) URL.revokeObjectURL(prev);
                return null;
            });
            setAudioPreviewError(null);
            return;
        }
        let cancelled = false;
        let createdUrl = null;
        const fetchPreview = async () => {
            setAudioPreviewLoading(true);
            setAudioPreviewError(null);
            setAudioPreviewUrl(prev => {
                if (prev) URL.revokeObjectURL(prev);
                return null;
            });
            try {
                const data = await generateAudioPreview({
                    celebId: preSelectedPersona.id,
                    wishText: preSelectedText,
                });
                if (cancelled) return;
                const audioBlob = new Blob(
                    [Uint8Array.from(atob(data.audio_base64), c => c.charCodeAt(0))],
                    {type: 'audio/mpeg'}
                );
                createdUrl = URL.createObjectURL(audioBlob);
                if (!cancelled) setAudioPreviewUrl(createdUrl);
            } catch (error) {
                if (!cancelled) {
                    console.error('Audio preview error:', error);
                    setAudioPreviewError('Не удалось сгенерировать аудио');
                }
            } finally {
                if (!cancelled) setAudioPreviewLoading(false);
            }
        };
        fetchPreview();
        return () => {
            cancelled = true;
            if (createdUrl) URL.revokeObjectURL(createdUrl);
        };
    }, [preSelectedText, preSelectedPersona?.id]);

    useEffect(() => () => {
        headerAudioRef.current?.pause();
    }, []);

    const audioCardSrc = audioPreviewUrl || null;
    const audioCardTitle = preSelectedPersona && preSelectedText
        ? `Поздравление от «${preSelectedPersona.name}»`
        : 'Превью поздравления';
    const audioCardSubtitle = 'Превью голоса · готов к разблокировке';

    const hasVideoPreview = !!audioPreviewUrl || audioPreviewLoading;

    const toggleHeaderPlay = (kind, src) => {
        if (!src) return;
        if (headerPlaying === kind) {
            headerAudioRef.current?.pause();
            setHeaderPlaying(null);
            return;
        }
        if (headerAudioRef.current) headerAudioRef.current.pause();
        const audio = new Audio(src);
        headerAudioRef.current = audio;
        audio.play().catch(() => setHeaderPlaying(null));
        audio.onended = () => setHeaderPlaying(null);
        setHeaderPlaying(kind);
    };

    const toggleExampleAudio = (id, src) => {
        if (!src) return;
        if (examplePlayingId === id) {
            exampleAudioRef.current?.pause();
            setExamplePlayingId(null);
            return;
        }
        exampleAudioRef.current?.pause();
        const audio = new Audio(src);
        exampleAudioRef.current = audio;
        audio.play().catch(() => setExamplePlayingId(null));
        audio.onended = () => setExamplePlayingId(null);
        setExamplePlayingId(id);
    };

    useEffect(() => () => {
        exampleAudioRef.current?.pause();
    }, []);

    const openCloudPaymentsWidget = (orderId, amount, planVideos, planId) => {
        const widget = new window.cp.CloudPayments();
        widget.pay('charge', {
            publicId: CLOUDPAYMENTS_PUBLIC_ID,
            description: `Покупка ${planVideos} видео`,
            amount: amount,
            currency: PAYMENT_CURRENCY,
            invoiceId: orderId,
            accountId: ACCOUNT_ID,
            paymentSchema: 'Single',
            data: {plan_id: planId, credits: planVideos, order_id: orderId}
        }, {
            onSuccess: () => {
                onPurchaseSuccess?.();
                setIsPurchasing(false);
            },
            onFail: () => {
                showError('Оплата не прошла. Попробуйте ещё раз.');
                setIsPurchasing(false);
            },
            onComplete: () => setIsPurchasing(false),
        });
    };

    const openYooKassaWidget = (confirmationToken) => {
        setShowYooKassaWidget(true);
        setTimeout(() => {
            if (yookassaWidgetRef.current) {
                const checkout = new window.YooMoneyCheckoutWidget({
                    confirmation_token: confirmationToken,
                    error_callback: (error) => {
                        console.error('YooKassa error:', error);
                        showError('Ошибка оплаты. Попробуйте ещё раз.');
                        setShowYooKassaWidget(false);
                        setIsPurchasing(false);
                    }
                });
                checkout.on('success', () => {
                    setShowYooKassaWidget(false);
                    onPurchaseSuccess?.();
                    setIsPurchasing(false);
                });
                checkout.on('fail', () => {
                    showError('Оплата не прошла. Попробуйте ещё раз.');
                    setShowYooKassaWidget(false);
                    setIsPurchasing(false);
                });
                checkout.render('yookassa-widget-container');
            }
        }, 100);
    };

    const handleSubscribe = async (planId) => {
        const plan = monthlyPlans.find(p => p.id === planId);
        if (!plan) return;

        trackEvent('start_subscription');
        setIsPurchasing(true);
        setPurchaseError(null);
        try {
            const yandexClientId = await getYandexClientId();
            const orderData = await createSubscription({
                accountId: ACCOUNT_ID,
                planId: plan.id,
                yandexClientId,
            });
            if (!orderData.confirmation_token) throw new Error('No confirmation token');
            openYooKassaWidget(orderData.confirmation_token);
        } catch (error) {
            console.error('Subscribe error:', error);
            setPurchaseError(error?.data?.detail || 'Ошибка оформления подписки. Попробуйте ещё раз.');
            setIsPurchasing(false);
        }
    };

    const handleTopup = async () => {
        const plan = topupPlans.find(p => p.id === selectedTopupId);
        if (!plan) return;

        trackEvent('start_topup');
        setIsPurchasing(true);
        setPurchaseError(null);
        try {
            const yandexClientId = await getYandexClientId();
            const orderData = await createTopup({
                accountId: ACCOUNT_ID,
                planId: plan.id,
                yandexClientId,
            });
            if (!orderData.purchase_id) throw new Error('No order ID');

            if (orderData.provider === 'yookassa' && orderData.confirmation_token) {
                openYooKassaWidget(orderData.confirmation_token);
            } else {
                openCloudPaymentsWidget(orderData.purchase_id, orderData.amount_rub, plan.videos, plan.id);
            }
        } catch (error) {
            console.error('Topup error:', error);
            setPurchaseError(error?.data?.detail || 'Ошибка создания заказа. Попробуйте ещё раз.');
            setIsPurchasing(false);
        }
    };

    const selectedTopup = topupPlans.find(p => p.id === selectedTopupId);
    const hasPreSelection = preSelectedPersona && preSelectedText;
    const hasPreviewReady = !!audioPreviewUrl && !audioPreviewLoading;
    const readyHeadline = context === 'skazki'
        ? 'Сказка почти готова'
        : context === 'music'
            ? 'Песня почти готова'
            : 'Видео от звезды почти готово';

    return (
        <div className="max-w-[1110px] mx-auto px-4 py-12 md:py-16" id="pricing">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-6">
                <h2 className="text-[28px] md:text-[42px] font-bold text-brand-dark leading-[1.1] tracking-[-0.42px]">
                    Тарифы
                </h2>
                <p className="text-base md:text-lg font-medium text-brand-dark/60">
                    Баланс: <span className="text-brand-red font-bold">{credits}</span> видео
                    {' '}&middot;{' '}
                    <span className="text-brand-red font-bold">{musicCredits}</span> песен
                    {' '}&middot;{' '}
                    <span className="text-brand-red font-bold">{skazkiCredits}</span> сказок
                </p>
            </div>

            <div
                className="bg-white rounded-[24px] shadow-[0_8px_40px_-12px_rgba(0,0,0,0.08)] border border-gray-100 p-5 md:p-8">
                <div className="flex flex-col lg:flex-row gap-6 lg:gap-10 mb-6">
                    <div className="flex-1 min-w-0">
                        <div className="inline-flex items-center gap-2 bg-brand-red/10 px-3 py-1.5 rounded-full mb-4">
                            <span className="w-1.5 h-1.5 rounded-full bg-brand-red"/>
                            <span className="text-brand-red text-[11px] font-semibold tracking-[0.06em] uppercase">
                                {hasPreviewReady ? 'Превью готово' : 'Подписка разблокирует всё'}
                            </span>
                        </div>
                        <h3 className="text-[26px] md:text-[36px] leading-[1.1] font-bold text-brand-dark tracking-[-0.42px]">
                            {hasPreviewReady ? (
                                <>{readyHeadline} — <span
                                    className="text-brand-red">осталось открыть финальную версию</span></>
                            ) : hasPreSelection ? (
                                <>Поздравление готовится — <span className="text-brand-red">оформите подписку, чтобы открыть</span></>
                            ) : (
                                <>Поздравляйте близких <span
                                    className="text-brand-red">видео, песнями и сказками</span> — каждый месяц</>
                            )}
                        </h3>
                    </div>
                    {hasVideoPreview && (
                        <div className="lg:w-[380px] flex flex-col gap-3">
                            <PlayerCard
                                title={audioCardTitle}
                                subtitle={
                                    audioPreviewError
                                        ? audioPreviewError
                                        : audioPreviewLoading
                                            ? 'Генерируем превью голоса…'
                                            : audioCardSubtitle
                                }
                                durationLabel="0:20"
                                isPlaying={headerPlaying === 'audio'}
                                isLoading={audioPreviewLoading}
                                onToggle={audioCardSrc ? () => toggleHeaderPlay('audio', audioCardSrc) : null}
                            />
                        </div>
                    )}
                </div>

                <div className="flex items-center justify-between gap-4 mb-6 pb-6 border-b border-gray-100">
                    <p className="text-[11px] font-semibold text-brand-dark/50 tracking-[0.08em] uppercase">
                        Подписка · разблокирует превью и больше
                    </p>
                    <p className="text-[11px] text-brand-dark/40 tracking-[0.04em] uppercase hidden sm:block">
                        отмена в один клик
                    </p>
                </div>

                {!hasActiveSubscription && monthlyPlans.length > 0 && (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                            {monthlyPlans.map(plan => {
                                const tier = tierKey(plan);
                                const isPro = tier === 'pro';
                                const isSelected = selectedSubId === plan.id;
                                const totalGreetings = plan.videos + (plan.songs || 0) + (plan.tales || 0);
                                const perGreeting = totalGreetings > 0 ? Math.round(plan.price / totalGreetings) : 0;
                                const proSavingsPct = isPro && litePerGreeting > 0 && perGreeting > 0
                                    ? Math.round((litePerGreeting - perGreeting) / litePerGreeting * 100)
                                    : 0;

                                return (
                                    <div
                                        key={plan.id}
                                        onClick={() => setSelectedSubId(plan.id)}
                                        className={`relative rounded-[20px] p-5 md:p-6 cursor-pointer transition border-2 ${isSelected ? 'border-brand-red bg-brand-red/[0.02] shadow-[0_4px_24px_-8px_rgba(255,107,107,0.4)]' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                                    >
                                        {isPro && plan.discount && (
                                            <div
                                                className="absolute -top-3 left-5 inline-flex items-center gap-1 bg-brand-dark text-white text-[10px] font-semibold px-2.5 py-1 rounded-full uppercase tracking-wide">
                                                <span className="text-brand-orange">★</span>
                                                Лучшая цена · -{plan.discount}%
                                            </div>
                                        )}
                                        <div
                                            className="absolute top-5 right-5 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors"
                                            style={{borderColor: isSelected ? '#ff6b6b' : '#d1d5db'}}>
                                            {isSelected && <div className="w-2.5 h-2.5 rounded-full bg-brand-red"/>}
                                        </div>

                                        <div
                                            className="inline-flex items-center gap-1.5 bg-brand-red/10 px-2.5 py-1 rounded-full mb-3">
                                            <span className="w-1 h-1 rounded-full bg-brand-red"/>
                                            <span
                                                className="text-brand-red text-[10px] font-semibold tracking-[0.06em] uppercase">
                                                Подписка · ежемесячно
                                            </span>
                                        </div>

                                        <h4 className="text-2xl md:text-[26px] font-bold text-brand-dark mb-2">
                                            {TIER_LABELS[tier] || plan.name}
                                        </h4>
                                        <p className="text-sm text-brand-dark/60 mb-5 leading-snug">
                                            {TIER_DESCRIPTIONS[tier]}
                                        </p>

                                        <div className="flex items-baseline gap-2 mb-1">
                                            <span
                                                className="text-[36px] md:text-[42px] font-bold text-brand-dark leading-none">
                                                {plan.price.toLocaleString()} ₽
                                            </span>
                                            <span className="text-sm text-brand-dark/50">/ мес</span>
                                            {plan.oldPrice && (
                                                <span className="text-sm text-brand-dark/40 line-through ml-1">
                                                    {plan.oldPrice.toLocaleString()} ₽
                                                </span>
                                            )}
                                            {plan.discount && (
                                                <span
                                                    className="bg-gray-100 text-brand-dark/60 text-[11px] font-semibold px-1.5 py-0.5 rounded">
                                                    -{plan.discount}%
                                                </span>
                                            )}
                                        </div>

                                        <p className="text-xs text-brand-dark/50 mb-5">
                                            ≈ <span
                                            className="font-semibold text-brand-dark/70">{perGreeting} ₽</span> за одно
                                            поздравление ·
                                            {' '}{isPro && proSavingsPct > 0
                                            ? <>на <span
                                                className="font-semibold text-brand-dark/70">{proSavingsPct}%</span> дешевле
                                                «Личного»</>
                                            : 'отмена в один клик'}
                                        </p>

                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleSubscribe(plan.id);
                                            }}
                                            disabled={isPurchasing}
                                            className={`w-full py-3.5 rounded-2xl font-semibold text-base transition disabled:opacity-50 ${
                                                isSelected
                                                    ? 'bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white hover:opacity-95 shadow-[0_4px_16px_-4px_rgba(255,107,107,0.6)]'
                                                    : 'bg-white border-2 border-gray-200 text-brand-dark hover:border-gray-300'
                                            }`}
                                        >
                                            {isPurchasing && isSelected
                                                ? 'Обработка…'
                                                : <>Подписаться
                                                    · {TIER_LABELS[tier] || plan.name}{!isSelected ? ' →' : ''}</>}
                                        </button>
                                    </div>
                                );
                            })}
                        </div>

                        {purchaseError && !isPurchasing && (
                            <div
                                className="mt-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                                {purchaseError}
                            </div>
                        )}

                        <div
                            className="mt-6 rounded-2xl bg-gradient-to-r from-brand-red/[0.04] via-brand-pink/[0.04] to-brand-orange/[0.04] border border-brand-red/15 px-4 md:px-5 py-4 flex items-start gap-3">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                 strokeWidth="2" className="text-brand-red shrink-0 mt-0.5">
                                <circle cx="12" cy="12" r="10"/>
                                <path d="M12 16v-4M12 8h.01"/>
                            </svg>
                            <div>
                                <p className="text-sm font-semibold text-brand-dark mb-0.5">
                                    Не хватит — докупите со скидкой
                                </p>
                                <p className="text-sm text-brand-dark/60 leading-snug">
                                    После подписки доступны разовые пакеты по сниженной цене за единицу. Эти кредиты не
                                    сгорают при следующем продлении.
                                </p>
                            </div>
                        </div>

                        <p className="text-xs text-brand-dark/40 mt-4 text-center">
                            Автопродление каждый месяц. Отменить можно в один клик в личном кабинете.
                        </p>
                    </>
                )}

                {hasActiveSubscription && topupPlans.length > 0 && (
                    <div>
                        <p className="text-brand-dark font-semibold text-lg mb-2">
                            Не хватило генераций? Купите дополнительный пакет
                        </p>
                        <p className="text-sm text-brand-dark/60 mb-5">
                            Дополнительные пакеты — разовая оплата со скидкой за объём. Эти кредиты не сгорают при
                            продлении подписки.
                        </p>
                        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5">
                            {topupPlans.map(plan => {
                                const isSelected = selectedTopupId === plan.id;
                                const isBest = plan.id === 'plan_3';
                                return (
                                    <div
                                        key={plan.id}
                                        onClick={() => setSelectedTopupId(plan.id)}
                                        className={`relative rounded-[16px] p-4 transition cursor-pointer border-2 ${isSelected ? 'border-brand-red bg-brand-red/[0.03]' : 'border-gray-200 bg-white hover:border-gray-300'}`}
                                    >
                                        {isBest && (
                                            <div
                                                className="absolute -top-2 left-1/2 -translate-x-1/2 bg-gradient-to-r from-brand-red to-brand-orange text-white text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap">
                                                лучшая цена
                                            </div>
                                        )}
                                        <div className="mb-2">
                                            <p className="text-base font-bold leading-snug text-brand-dark">
                                                {plan.videos} видео
                                                {plan.songs > 0 && <><br/>+ {plan.songs_text}</>}
                                                {plan.tales > 0 && <><br/>+ {plan.tales_text}</>}
                                            </p>
                                        </div>
                                        <p className="text-xs text-brand-dark/50 mb-3">
                                            {TOPUP_DESCRIPTIONS[plan.id] || ''}
                                        </p>
                                        <div className="flex items-baseline gap-1">
                                            <span
                                                className="text-2xl font-bold text-brand-dark">{plan.price.toLocaleString()}</span>
                                            <span className="text-sm text-brand-dark/50">₽</span>
                                        </div>
                                        {plan.discount && (
                                            <div className="mt-1 flex items-center gap-2">
                                                <span
                                                    className="text-xs text-brand-dark/40 line-through">{plan.oldPrice?.toLocaleString()} ₽</span>
                                                <span
                                                    className="bg-gradient-to-r from-brand-red to-brand-orange text-white text-[10px] px-1.5 py-0.5 rounded-full font-medium">
                                                    -{plan.discount}%
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {purchaseError && !isPurchasing && (
                            <div
                                className="mb-3 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
                                {purchaseError}
                            </div>
                        )}

                        <button
                            onClick={handleTopup}
                            disabled={isPurchasing || !selectedTopup}
                            className="w-full py-4 rounded-2xl bg-gradient-to-r from-brand-red via-brand-pink to-brand-orange text-white text-base font-semibold tracking-[-0.16px] hover:opacity-95 transition disabled:opacity-50"
                        >
                            {isPurchasing
                                ? 'Обработка…'
                                : hasPreSelection
                                    ? 'Купить и начать генерацию'
                                    : `Купить ${selectedTopup?.videos || 1} видео${selectedTopup?.songs ? ` + ${selectedTopup.songs} песен` : ''}${selectedTopup?.tales ? ` + ${selectedTopup.tales} сказок` : ''}`
                            }
                        </button>
                    </div>
                )}

                {hasActiveSubscription && (
                    <div className="mt-6 pt-6 border-t border-gray-100">
                        <p className="text-[11px] font-semibold text-brand-dark/50 tracking-[0.08em] uppercase mb-3">
                            Привязать аккаунт к боту
                        </p>
                        <p className="text-sm text-brand-dark/60 mb-3 leading-snug">
                            Подписка с сайта будет доступна в боте — и вы не потеряете её при смене браузера.
                        </p>
                        <div className="flex flex-wrap gap-2">
                            <LinkAccountButton platform="telegram" isLinked={!!linkedTelegramId} size="sm"/>
                            <LinkAccountButton platform="max" isLinked={!!linkedMaxId} size="sm"/>
                        </div>
                    </div>
                )}

                <div className="mt-8 pt-8 border-t border-gray-100">
                    <div className="flex items-center justify-between mb-4">
                        <p className="text-[11px] font-semibold text-brand-dark/50 tracking-[0.08em] uppercase">
                            Что внутри · примеры
                        </p>
                        <p className="text-[11px] text-brand-dark/40 tracking-[0.04em]">
                            {PAYWALL_EXAMPLES.length} работ
                        </p>
                    </div>
                    <div
                        className="flex gap-3 overflow-x-auto -mx-5 md:-mx-8 px-5 md:px-8 pb-2 snap-x snap-mandatory"
                        style={{scrollbarWidth: 'thin'}}
                    >
                        {PAYWALL_EXAMPLES.map(ex => {
                            const isAudio = ex.kind === 'music' || ex.kind === 'skazki';
                            const isPlaying = examplePlayingId === ex.id;
                            const onClick = isAudio
                                ? () => toggleExampleAudio(ex.id, ex.audioUrl)
                                : () => setExampleVideo(ex);
                            return (
                                <button
                                    key={ex.id}
                                    onClick={onClick}
                                    className={`relative shrink-0 w-[150px] h-[200px] rounded-2xl overflow-hidden bg-gradient-to-br ${ex.gradient} text-left snap-start group focus:outline-none focus:ring-2 focus:ring-brand-red/40`}
                                >
                                    {ex.thumbnail && (
                                        <img
                                            src={ex.thumbnail}
                                            alt=""
                                            loading="lazy"
                                            className="absolute inset-0 w-full h-full object-cover"
                                        />
                                    )}

                                    <span
                                        className="absolute top-2 left-2 z-10 inline-flex items-center gap-1 bg-white/95 text-brand-red text-[9px] font-bold tracking-[0.06em] uppercase px-2 py-0.5 rounded-full">
                                        <span className="w-1 h-1 rounded-full bg-brand-red"/>
                                        {EXAMPLE_BADGE[ex.kind]}
                                    </span>

                                    <div className="absolute inset-0 flex items-center justify-center">
                                        {isAudio && !ex.thumbnail ? (
                                            <div className="flex items-end gap-[3px] h-10">
                                                {[40, 70, 90, 60, 80, 50, 95, 70, 45, 80, 60].map((h, i) => (
                                                    <span
                                                        key={i}
                                                        className={`w-[3px] rounded-full bg-white/85 ${isPlaying ? 'animate-pulse' : ''}`}
                                                        style={{height: `${h}%`, animationDelay: `${i * 80}ms`}}
                                                    />
                                                ))}
                                            </div>
                                        ) : (
                                            <span
                                                className="w-12 h-12 rounded-full bg-white/95 flex items-center justify-center shadow-md">
                                                {isAudio && isPlaying ? (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#0E0E0E">
                                                        <rect x="6" y="4" width="4" height="16" rx="1"/>
                                                        <rect x="14" y="4" width="4" height="16" rx="1"/>
                                                    </svg>
                                                ) : (
                                                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#0E0E0E">
                                                        <path d="M8 5v14l11-7z"/>
                                                    </svg>
                                                )}
                                            </span>
                                        )}
                                    </div>

                                    <div
                                        className="absolute left-0 right-0 bottom-0 px-3 py-2.5 bg-gradient-to-t from-black/70 via-black/30 to-transparent">
                                        <p className="text-white font-bold text-[14px] leading-tight">{ex.title}</p>
                                        {ex.subtitle && (
                                            <p className="text-white/80 text-[11px] leading-tight mt-0.5 truncate">
                                                {ex.subtitle}
                                            </p>
                                        )}
                                    </div>
                                </button>
                            );
                        })}
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-6">
                        {PAYWALL_GUARANTEES.map(g => (
                            <div
                                key={g.title}
                                className="flex items-start gap-3 bg-gray-50 border border-gray-100 rounded-xl px-4 py-3"
                            >
                                <span
                                    className="shrink-0 w-8 h-8 rounded-full bg-white text-brand-red flex items-center justify-center border border-brand-red/15">
                                    {g.icon}
                                </span>
                                <div className="min-w-0">
                                    <p className="text-[13px] font-semibold text-brand-dark leading-tight">{g.title}</p>
                                    <p className="text-[11px] text-brand-dark/55 leading-snug mt-0.5">{g.subtitle}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {exampleVideo && (
                <div
                    className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
                    onClick={() => setExampleVideo(null)}
                >
                    <div className="relative w-full max-w-[420px]" onClick={e => e.stopPropagation()}>
                        <button
                            onClick={() => setExampleVideo(null)}
                            aria-label="Закрыть"
                            className="absolute -top-10 right-0 w-8 h-8 rounded-full bg-white/15 hover:bg-white/25 text-white flex items-center justify-center transition"
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2"
                                      strokeLinecap="round"/>
                            </svg>
                        </button>
                        <video
                            src={exampleVideo.videoUrl}
                            controls
                            autoPlay
                            playsInline
                            className="w-full rounded-2xl bg-black aspect-[9/16] object-cover"
                        />
                    </div>
                </div>
            )}

            {showYooKassaWidget && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
                    <div className="relative bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
                        <button
                            onClick={() => {
                                setShowYooKassaWidget(false);
                                setIsPurchasing(false);
                            }}
                            className="absolute top-4 right-4 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-gray-600 hover:bg-gray-200 transition"
                        >
                            <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                                <path d="M1 1L13 13M1 13L13 1" stroke="currentColor" strokeWidth="2"
                                      strokeLinecap="round"/>
                            </svg>
                        </button>
                        <h3 className="text-xl font-bold text-brand-dark mb-4">Оплата</h3>
                        <div id="yookassa-widget-container" ref={yookassaWidgetRef}></div>
                    </div>
                </div>
            )}
        </div>
    );
}
